import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Check, CheckCircle, CirclesThreePlus, CloudArrowUp,
  DotsSixVertical, Images, LinkSimple, NotePencil, Palette, Sparkle, Trash, X
} from '@phosphor-icons/react';

// PRODUCT-IA-CLEANUP-001A: extracted from the historical CreativeStudioTab
// — this is the Moodboard contextual destination inside the DNA
// workspace. Connector/MCP-workbench functionality moved to
// CreativeToolsPanel (now under Connections / Creative Tools) instead of
// living alongside the moodboard; this component owns only reference-
// image upload/library-import/reorder/notes/select-for-DNA and the
// "Use for DNA" hand-off into the Design sub-tab.
export default function MoodboardSection({ api, project, onOpenDesign }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [studio, setStudio] = useState({ moodboard: [], active_dna: null });
  const [library, setLibrary] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySelection, setLibrarySelection] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [noteEditingId, setNoteEditingId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  const selectedCount = useMemo(
    () => studio.moodboard.filter(asset => asset.selected_for_dna).length,
    [studio.moodboard]
  );

  const loadStudio = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/creative-studio`);
      setStudio(data);
    } catch (e) {
      toast.error(t('project.creative.loadError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project?.id) loadStudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const uploadFiles = async (files) => {
    const images = Array.from(files || []).filter(file => file.type.startsWith('image/'));
    if (!images.length) return;
    setUploading(true);
    const form = new FormData();
    images.forEach(file => form.append('files', file));
    try {
      const { data } = await api.post(`/projects/${project.id}/creative-studio/moodboard`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStudio(prev => ({ ...prev, moodboard: [...prev.moodboard, ...(data.assets || [])] }));
      toast.success(t('project.creative.uploadSuccess', { count: data.assets?.length || images.length }));
    } catch (e) {
      toast.error(t('project.creative.uploadError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setUploading(false);
    }
  };

  const openLibrary = async () => {
    setLibraryOpen(true);
    try {
      const { data } = await api.get(`/media/library/${project.id}`);
      setLibrary((data || []).filter(item => item.type === 'image' || String(item.mime_type || '').startsWith('image/')));
    } catch (e) {
      toast.error(t('project.creative.libraryError', { message: e.response?.data?.detail || e.message }));
    }
  };

  const importLibrary = async () => {
    if (!librarySelection.size) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/creative-studio/moodboard/import`, {
        media_ids: Array.from(librarySelection),
      });
      setStudio(prev => ({ ...prev, moodboard: [...prev.moodboard, ...(data.assets || [])] }));
      setLibrarySelection(new Set());
      setLibraryOpen(false);
      toast.success(t('project.creative.importSuccess', { count: data.assets?.length || 0 }));
    } catch (e) {
      toast.error(t('project.creative.importError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setImporting(false);
    }
  };

  const patchAsset = async (assetId, updates, optimistic = true) => {
    const previous = studio.moodboard;
    if (optimistic) {
      setStudio(prev => ({
        ...prev,
        moodboard: prev.moodboard.map(asset => asset.id === assetId ? { ...asset, ...updates } : asset),
      }));
    }
    try {
      await api.patch(`/projects/${project.id}/creative-studio/moodboard/${assetId}`, updates);
    } catch (e) {
      setStudio(prev => ({ ...prev, moodboard: previous }));
      toast.error(t('project.creative.updateError', { message: e.response?.data?.detail || e.message }));
    }
  };

  const saveNote = async (assetId) => {
    await patchAsset(assetId, { notes: noteDraft });
    setNoteEditingId(null);
    setNoteDraft('');
  };

  const deleteAsset = async (assetId) => {
    try {
      await api.delete(`/projects/${project.id}/creative-studio/moodboard/${assetId}`);
      setStudio(prev => ({ ...prev, moodboard: prev.moodboard.filter(asset => asset.id !== assetId) }));
    } catch (e) {
      toast.error(t('project.creative.deleteError', { message: e.response?.data?.detail || e.message }));
    }
  };

  const reorderAsset = async (targetId) => {
    if (!draggedId || draggedId === targetId) return;
    const items = [...studio.moodboard];
    const from = items.findIndex(item => item.id === draggedId);
    const to = items.findIndex(item => item.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    const reordered = items.map((item, index) => ({ ...item, sort_order: index }));
    setStudio(prev => ({ ...prev, moodboard: reordered }));
    setDraggedId(null);
    try {
      await Promise.all(reordered.map(item => (
        api.patch(`/projects/${project.id}/creative-studio/moodboard/${item.id}`, { sort_order: item.sort_order })
      )));
    } catch (e) {
      toast.error(t('project.creative.orderError', { message: e.response?.data?.detail || e.message }));
      await loadStudio();
    }
  };

  const useForDna = async () => {
    setPromoting(true);
    const tid = toast.loading(t('project.creative.preparingDna'));
    try {
      const { data } = await api.post(`/projects/${project.id}/creative-studio/moodboard/use-for-dna`);
      toast.success(t('project.creative.dnaReady', { count: data.promoted }), { id: tid });
      onOpenDesign?.();
    } catch (e) {
      toast.error(t('project.creative.dnaError', { message: e.response?.data?.detail || e.message }), { id: tid });
    } finally {
      setPromoting(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)] py-8">{t('common.loading')}</p>;
  }

  return (
    <div className="pt-2 pb-12 space-y-5">
      <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.4fr)] gap-5">
        <section className="card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><Images size={20} className="text-amber-300" /> {t('project.creative.moodboardTitle')}</h3>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('project.creative.moodboardHint')}</p>
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost text-xs" onClick={openLibrary}><LinkSimple size={14} /> {t('project.creative.fromLibrary')}</button>
              <button className="btn-gradient text-xs" onClick={() => inputRef.current?.click()} disabled={uploading}>
                <CloudArrowUp size={14} /> {uploading ? t('project.creative.uploading') : t('project.creative.upload')}
              </button>
              <input ref={inputRef} className="hidden" type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={event => uploadFiles(event.target.files)} />
            </div>
          </div>

          <div
            className={`rounded-2xl border border-dashed p-5 text-center transition-colors ${dragOver ? 'border-amber-300 bg-amber-300/5' : 'border-[var(--border-color)]'}`}
            onDragOver={event => { event.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={event => { event.preventDefault(); setDragOver(false); uploadFiles(event.dataTransfer.files); }}
          >
            <CloudArrowUp size={24} className="mx-auto text-amber-300 mb-2" />
            <p className="text-sm">{t('project.creative.dropTitle')}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">{t('project.creative.dropHint')}</p>
          </div>

          {studio.moodboard.length === 0 ? (
            <div className="py-14 text-center">
              <CirclesThreePlus size={34} className="mx-auto text-[var(--text-muted)] mb-3" />
              <p className="text-sm text-[var(--text-muted)]">{t('project.creative.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mt-5">
              {studio.moodboard.map(asset => (
                <article
                  key={asset.id}
                  draggable
                  onDragStart={() => setDraggedId(asset.id)}
                  onDragOver={event => event.preventDefault()}
                  onDrop={() => reorderAsset(asset.id)}
                  className="group rounded-2xl overflow-hidden border bg-white/[0.025]"
                  style={{ borderColor: asset.selected_for_dna ? 'rgba(244,184,96,0.55)' : 'var(--border-color)' }}
                >
                  <div className="aspect-[4/5] bg-black/30 relative overflow-hidden">
                    <img src={asset.url} alt={asset.original_name} className="w-full h-full object-cover" />
                    <button
                      className="absolute top-2 right-2 h-8 w-8 rounded-full flex items-center justify-center border border-white/10 backdrop-blur-md"
                      style={{ background: asset.selected_for_dna ? '#d7a33d' : 'rgba(5,8,16,0.72)', color: asset.selected_for_dna ? '#050505' : '#fff' }}
                      title={t('project.creative.selectForDna')}
                      onClick={() => patchAsset(asset.id, { selected_for_dna: !asset.selected_for_dna })}
                    >
                      {asset.selected_for_dna ? <Check weight="bold" size={15} /> : <Sparkle size={15} />}
                    </button>
                    <span className="absolute top-2 left-2 rounded-lg bg-black/65 px-2 py-1 text-[9px] uppercase tracking-wide">
                      {asset.source === 'media_library'
                        ? t('project.creative.librarySource')
                        : asset.source?.startsWith('mcp:')
                          ? t('project.creative.mcpSource')
                          : t('project.creative.uploadSource')}
                    </span>
                  </div>
                  <div className="p-2.5">
                    <div className="flex items-center gap-2">
                      <DotsSixVertical size={15} className="text-[var(--text-muted)] cursor-grab flex-shrink-0" />
                      <p className="text-[10px] truncate flex-1">{asset.original_name}</p>
                      <button onClick={() => { setNoteEditingId(asset.id); setNoteDraft(asset.notes || ''); }} title={t('project.creative.addNote')}><NotePencil size={14} /></button>
                      <button className="text-[var(--accent-pink)]" onClick={() => deleteAsset(asset.id)} title={t('common.delete')}><Trash size={14} /></button>
                    </div>
                    {asset.notes && noteEditingId !== asset.id && <p className="text-[10px] text-[var(--text-muted)] mt-2 line-clamp-2">{asset.notes}</p>}
                    {noteEditingId === asset.id && (
                      <div className="mt-2">
                        <textarea className="input-dark text-xs min-h-20 w-full p-2" value={noteDraft} onChange={event => setNoteDraft(event.target.value)} placeholder={t('project.creative.notePlaceholder')} />
                        <div className="flex justify-end gap-1 mt-1">
                          <button className="btn-ghost text-[10px] py-1 px-2" onClick={() => setNoteEditingId(null)}>{t('common.cancel')}</button>
                          <button className="btn-gradient text-[10px] py-1 px-2" onClick={() => saveNote(asset.id)}>{t('common.save')}</button>
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-[var(--border-color)] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-xs text-[var(--text-muted)]">{t('project.creative.selectedCount', { count: selectedCount })}</p>
            <button className="btn-gradient text-xs" onClick={useForDna} disabled={promoting || selectedCount < 2}>
              <Palette size={15} /> {promoting ? t('project.creative.preparingDna') : t('project.creative.useForDna')}
            </button>
          </div>
        </section>

        <aside>
          <section className="card">
            <h3 className="font-semibold flex items-center gap-2"><CheckCircle size={19} className="text-emerald-300" /> {t('project.creative.dnaStatusTitle')}</h3>
            {studio.active_dna ? (
              <div className="mt-3 rounded-2xl p-4 border border-emerald-400/20 bg-emerald-400/5">
                <p className="text-sm text-emerald-300">{t('project.creative.dnaActive')}</p>
                <p className="text-[10px] text-[var(--text-muted)] mt-1">{t('project.creative.dnaFingerprint', { value: studio.active_dna.generated_prompt_fingerprint || '-' })}</p>
              </div>
            ) : (
              <p className="text-xs text-[var(--text-muted)] mt-3">{t('project.creative.noDna')}</p>
            )}
            <button className="btn-ghost text-xs w-full mt-3" onClick={onOpenDesign}><Palette size={14} /> {t('project.creative.openDesign')}</button>
          </section>
        </aside>
      </div>

      {libraryOpen && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={event => { if (event.target === event.currentTarget) setLibraryOpen(false); }}>
          <div className="w-full max-w-5xl max-h-[85vh] rounded-[28px] border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden flex flex-col">
            <div className="p-4 md:p-5 border-b border-[var(--border-color)] flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{t('project.creative.libraryTitle')}</h3>
                <p className="text-xs text-[var(--text-muted)] mt-1">{t('project.creative.libraryHint')}</p>
              </div>
              <button className="btn-ghost p-2" onClick={() => setLibraryOpen(false)}><X size={16} /></button>
            </div>
            <div className="p-4 md:p-5 overflow-y-auto">
              {library.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] py-12 text-center">{t('project.creative.libraryEmpty')}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {library.map(item => {
                    const selected = librarySelection.has(item.id);
                    return (
                      <button
                        key={item.id}
                        className="relative aspect-[4/5] rounded-2xl overflow-hidden border"
                        style={{ borderColor: selected ? '#d7a33d' : 'var(--border-color)' }}
                        onClick={() => setLibrarySelection(prev => {
                          const next = new Set(prev);
                          if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                          return next;
                        })}
                      >
                        <img src={item.url} alt={item.original_name || item.filename} className="w-full h-full object-cover" />
                        {selected && <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-amber-300 text-black flex items-center justify-center"><Check size={14} weight="bold" /></span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[var(--border-color)] flex items-center justify-between">
              <p className="text-xs text-[var(--text-muted)]">{t('project.creative.librarySelected', { count: librarySelection.size })}</p>
              <button className="btn-gradient text-xs" onClick={importLibrary} disabled={!librarySelection.size || importing}>
                {importing ? t('project.creative.importing') : t('project.creative.import')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
